/**
 * DurationBar: time spent per order state (New, Accepted, Preparing, Ready) as one proportional bar.
 * Props: caption, segments [{ key, label, minutes }] (first 4 used), formatValue (minutes -> "4 min"),
 *        compact? (6 px bar only, for an order card; the data stays in the hidden table and in tooltips),
 *        labelHeading?, durationLabel? (table header, default "Value"), loading?, className?.
 * Full variant shows a legend with each duration and the total.
 */
import { useTranslation } from 'react-i18next';
import { StackedBar } from './StackedBar';

export interface DurationSegment {
    key: string;
    label: string;
    minutes: number;
}

export interface DurationBarProps {
    caption: string;
    segments: DurationSegment[];
    formatValue: (minutes: number) => string;
    compact?: boolean;
    labelHeading?: string;
    durationLabel?: string;
    loading?: boolean;
    className?: string;
}

export const DurationBar = ({ caption, segments, formatValue, compact, labelHeading, durationLabel, loading, className }: DurationBarProps) => {
    const { t } = useTranslation('charts');
    const used = segments.slice(0, 4);
    const total = used.reduce((s, p) => s + Math.max(p.minutes, 0), 0);
    return (
        <StackedBar
            caption={caption}
            compact={compact}
            loading={loading}
            className={className}
            parts={used.map((s) => ({ key: s.key, label: s.label, value: s.minutes, text: formatValue(s.minutes) }))}
            headers={[labelHeading ?? t('item'), durationLabel ?? t('value')]}
            rows={used.length === 0 ? [] : [...used.map((s) => [s.label, formatValue(s.minutes)]), [t('total'), formatValue(total)]]}
            footer={
                <p className="mt-2 text-small text-muted">
                    {t('total')} <span className="tabular-nums text-strong">{formatValue(total)}</span>
                </p>
            }
        />
    );
};
